import React, { useEffect, useState } from 'react';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { documentsAPI } from '../../api/client';
import toast from 'react-hot-toast';

const PREVIEW_TYPES = new Set(['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']);
const IMAGE_TYPES   = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']);

export default function FilePreviewModal({ doc, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const ext = (doc.file_type || '').toLowerCase();
  const canPreview = PREVIEW_TYPES.has(ext);
  const isImage    = IMAGE_TYPES.has(ext);

  useEffect(() => {
    if (!canPreview) return;
    let url;
    setLoading(true);
    setLoadError(null);
    documentsAPI.viewBlob(doc.id)
      .then((res) => {
        url = URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' }));
        setBlobUrl(url);
      })
      .catch(() => setLoadError('Failed to load preview.'))
      .finally(() => setLoading(false));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [doc.id, canPreview]);

  const handleDownload = async () => {
    try {
      const res = await documentsAPI.downloadBlob(doc.id);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-4xl max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
          <p className="text-sm font-semibold text-gray-800 truncate max-w-[65%]">
            {doc.original_filename}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 hover:border-gray-300 rounded-lg transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden rounded-b-2xl">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          )}

          {loadError && (
            <div className="flex flex-col items-center justify-center h-64 gap-4 text-gray-500">
              <p className="text-sm">{loadError}</p>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download instead
              </button>
            </div>
          )}

          {!loading && !loadError && blobUrl && (
            isImage ? (
              <div className="flex items-center justify-center h-full p-6 overflow-auto bg-gray-50 rounded-b-2xl">
                <img
                  src={blobUrl}
                  alt={doc.original_filename}
                  className="max-w-full max-h-full object-contain rounded"
                />
              </div>
            ) : (
              <iframe
                src={blobUrl}
                title={doc.original_filename}
                className="w-full border-0 rounded-b-2xl"
                style={{ height: '70vh' }}
              />
            )
          )}

          {!canPreview && !loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                <span className="text-lg font-bold text-gray-400 uppercase">{ext || '?'}</span>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700">Preview not available</p>
                <p className="text-xs text-gray-400 mt-1">Download the file to open it locally</p>
              </div>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download {doc.original_filename}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
